import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, Bar, BarChart, ComposedChart, Line, ReferenceLine, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { formatCents } from '@/lib/format/currency';
import {
  buildBudgetBurndown,
  burndownChartFrame,
  daysBetween,
  saoPauloDay,
  type BudgetBurndown,
} from '@/lib/campaigns/budgetBurndown';

// The app's lime (the Analytics "Visão Geral" cards use the same).
const LIME = 'hsl(75 100% 50%)';
const BAR_GREEN = '#4ade80';

export type BudgetPaceCampaign = {
  status: string;
  currency?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  budget_total_cents: number;
  budget_remaining_cents: number;
};
export type BudgetPaceStats = {
  daily_spend?: Array<{ date: string; spend_cents: number }> | null;
  daily_series?: Array<{ date: string; total_views: string }> | null;
};

type Row = {
  x: number;
  date: string;
  kind: 'start' | 'day' | 'projection';
  remaining: number | null;
  projected: number | null;
  spendCents: number;
  today?: boolean;
};

const shortDay = (day: string) => `${day.slice(8, 10)}/${day.slice(5, 7)}`;

function Tile({ label, value, hint, warn = false }: { label: string; value: string; hint: string; warn?: boolean }) {
  return (
    <div className="min-w-0 rounded-lg border border-border/60 px-3 py-2.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="truncate text-xl font-bold tabular-nums">{value}</p>
      <p className={`text-xs ${warn ? 'text-amber-500' : 'text-muted-foreground'}`}>{hint}</p>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return <h3 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{children}</h3>;
}

/**
 * The CPM campaign's budget burndown for admins/auditors: what is left, the
 * pace of the last complete days and when the money runs out. The customer PDF
 * draws the same model (`buildBudgetBurndown`).
 */
export function CampaignBudgetPaceCard({ campaign, stats, now = Date.now() }: {
  campaign: BudgetPaceCampaign;
  stats: BudgetPaceStats;
  now?: number;
}) {
  const { t } = useTranslation();
  const currency = campaign.currency || 'BRL';
  const money = (cents: number) => formatCents(Math.round(cents), currency);
  const compactMoney = (cents: number) => new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1,
  }).format(cents / 100);

  const todayDay = saoPauloDay(now);
  const burndown: BudgetBurndown | null = useMemo(() => {
    if (!Array.isArray(stats.daily_spend) || !todayDay) return null;
    return buildBudgetBurndown({
      totalCents: campaign.budget_total_cents,
      remainingCents: campaign.budget_remaining_cents,
      startDate: saoPauloDay(campaign.start_date),
      endDate: saoPauloDay(campaign.end_date),
      active: campaign.status === 'active',
      today: todayDay,
      dailySpend: stats.daily_spend.map((point) => ({ date: point.date, spendCents: point.spend_cents })),
      cumulativeViews: (stats.daily_series || []).map((point) => ({
        date: point.date, views: Number.parseInt(point.total_views, 10) || 0,
      })),
    });
  }, [campaign.budget_total_cents, campaign.budget_remaining_cents, campaign.start_date, campaign.end_date,
    campaign.status, stats.daily_spend, stats.daily_series, todayDay]);

  if (!burndown || burndown.totalCents <= 0) return null;

  const { first, today, end, projection, last } = burndownChartFrame(burndown);
  const dayX = (day: string) => daysBetween(first, day);
  const showEnd = end !== null;

  const rows: Row[] = burndown.points.map((point) => ({
    x: dayX(point.date),
    date: point.date,
    kind: point.start ? 'start' : 'day',
    remaining: point.remainingCents / 100,
    projected: point.today && projection.length ? point.remainingCents / 100 : null,
    spendCents: point.spendCents,
    today: point.today,
  }));
  if (projection.length === 2) {
    const to = projection[1];
    rows.push({ x: dayX(to.date), date: to.date, kind: 'projection', remaining: null, projected: to.remainingCents / 100, spendCents: 0 });
  }
  const projectionEnd = projection.length === 2 ? projection[1].date : today;
  const lastX = dayX(last);
  const ticks = [0, dayX(today)];
  if (projection.length === 2 && projectionEnd !== end) ticks.push(dayX(projectionEnd));
  const tickLabel = new Map<number, string>([
    [0, t('admin.campaigns.submissions.budgetPace.start')],
    [dayX(today), t('admin.campaigns.submissions.budgetPace.today')],
  ]);
  if (projection.length === 2 && !tickLabel.has(dayX(projectionEnd))) {
    tickLabel.set(dayX(projectionEnd), burndown.outlook.kind === 'runs-out' ? `~${shortDay(projectionEnd)}` : shortDay(projectionEnd));
  }

  // The outlook speaks of the campaign's end even when the chart leaves it out (over six months away).
  const campaignEnd = burndown.endDate;
  const endLabel = campaignEnd ? shortDay(campaignEnd) : '';
  let outlookValue = t('admin.campaigns.submissions.budgetPace.noForecast');
  let outlookHint = t('admin.campaigns.submissions.budgetPace.tooYoung');
  let warn = false;
  switch (burndown.outlook.kind) {
    case 'runs-out':
      outlookValue = t('admin.campaigns.submissions.budgetPace.runsOut', { date: shortDay(burndown.outlook.date) });
      if (!campaignEnd) outlookHint = t('admin.campaigns.submissions.budgetPace.atCurrentPace');
      else if (burndown.outlook.date <= campaignEnd) {
        outlookHint = t('admin.campaigns.submissions.budgetPace.beforeEnd', { date: endLabel });
      } else if (burndown.remainingAtEndCents !== null) {
        outlookHint = t('admin.campaigns.submissions.budgetPace.leftAtEnd', { date: endLabel, amount: money(burndown.remainingAtEndCents) });
      } else outlookHint = t('admin.campaigns.submissions.budgetPace.afterEnd', { date: endLabel });
      break;
    case 'beyond-6-months':
      outlookValue = t('admin.campaigns.submissions.budgetPace.beyond');
      outlookHint = campaignEnd && burndown.remainingAtEndCents !== null
        ? t('admin.campaigns.submissions.budgetPace.leftAtEnd', { date: endLabel, amount: money(burndown.remainingAtEndCents) })
        : t('admin.campaigns.submissions.budgetPace.atCurrentPace');
      break;
    case 'no-recent-spend':
      outlookHint = t('admin.campaigns.submissions.budgetPace.noRecentSpend', { count: burndown.outlook.days });
      warn = true;
      break;
    case 'exhausted':
      outlookValue = t('admin.campaigns.submissions.budgetPace.exhausted');
      outlookHint = t('admin.campaigns.submissions.budgetPace.exhaustedHint');
      break;
    case 'inactive':
      outlookHint = t('admin.campaigns.submissions.budgetPace.inactive');
      break;
  }

  const config = {
    remaining: { label: t('admin.campaigns.submissions.budgetPace.remainingSeries'), color: LIME },
    projected: { label: t('admin.campaigns.submissions.budgetPace.projectionSeries'), color: LIME },
  } satisfies ChartConfig;
  const viewsConfig = { views: { label: t('admin.campaigns.submissions.budgetPace.weeklyViews'), color: BAR_GREEN } } satisfies ChartConfig;
  const weeks = burndown.weeks.map((week) => ({ ...week, value: week.views ?? 0 }));

  return (
    <Card data-testid="cpm-budget-pace-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('admin.campaigns.submissions.budgetPace.title')}</CardTitle>
        <p className="text-xs text-muted-foreground">{t('admin.campaigns.submissions.budgetPace.subtitle')}</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Tile
            label={t('admin.campaigns.submissions.budgetPace.remaining')}
            value={money(Math.max(0, burndown.remainingCents))}
            hint={t('admin.campaigns.submissions.budgetPace.remainingHint', {
              total: money(burndown.totalCents),
              percent: new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(burndown.usedPercent ?? 0),
            })}
          />
          <Tile
            label={t('admin.campaigns.submissions.budgetPace.pace')}
            value={burndown.ratePerWeekCents === null ? '—' : money(burndown.ratePerWeekCents)}
            hint={burndown.rateDays
              ? t('admin.campaigns.submissions.budgetPace.paceHint', { count: burndown.rateDays })
              : t('admin.campaigns.submissions.budgetPace.paceEmpty')}
          />
          <Tile label={t('admin.campaigns.submissions.budgetPace.outlook')} value={outlookValue} hint={outlookHint} warn={warn} />
        </div>

        <div>
          <SectionLabel>{t('admin.campaigns.submissions.budgetPace.chartTitle')}</SectionLabel>
          <ChartContainer
            config={config}
            className="aspect-auto h-48 w-full"
            aria-label={t('admin.campaigns.submissions.budgetPace.chartLabel')}
          >
            <ComposedChart data={rows} margin={{ top: 14, right: 12, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="x"
                type="number"
                domain={[0, lastX]}
                ticks={ticks}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value: number) => tickLabel.get(value) ?? ''}
                height={20}
              />
              <YAxis
                width={64}
                tickCount={3}
                tickLine={false}
                axisLine={false}
                domain={[(min: number) => Math.min(0, min), 'dataMax']}
                tickFormatter={(value: number) => compactMoney(value * 100)}
              />
              <ReferenceLine y={0} stroke="hsl(var(--border))" />
              {showEnd && end && (
                <ReferenceLine
                  x={dayX(end)}
                  stroke="hsl(var(--muted-foreground))"
                  strokeDasharray="3 3"
                  label={{
                    value: `${t('admin.campaigns.submissions.budgetPace.end')} ${shortDay(end)}`,
                    position: 'insideTopRight',
                    fontSize: 10,
                    fill: 'hsl(var(--muted-foreground))',
                  }}
                />
              )}
              <ChartTooltip
                cursor={{ stroke: 'hsl(var(--border))' }}
                content={({ active, payload }) => {
                  const row = payload?.[0]?.payload as Row | undefined;
                  if (!active || !row) return null;
                  let title = shortDay(row.date);
                  const lines: Array<[string, string]> = [];
                  if (row.kind === 'start') title = t('admin.campaigns.submissions.budgetPace.tooltipStart', { amount: money(burndown.totalCents) });
                  else if (row.kind === 'projection') {
                    title = burndown.outlook.kind === 'runs-out' && row.date === burndown.outlook.date
                      ? t('admin.campaigns.submissions.budgetPace.tooltipProjection', { date: shortDay(row.date) })
                      : t('admin.campaigns.submissions.budgetPace.tooltipProjectionEnd', { date: shortDay(row.date) });
                    lines.push([t('admin.campaigns.submissions.budgetPace.remainingSeries'), `~${money((row.projected ?? 0) * 100)}`]);
                  } else {
                    lines.push([t('admin.campaigns.submissions.budgetPace.remainingSeries'), money((row.remaining ?? 0) * 100)]);
                    lines.push([t('admin.campaigns.submissions.budgetPace.tooltipSpend'), money(row.spendCents)]);
                  }
                  return (
                    <div className="min-w-40 rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
                      <div className="font-medium">{title}</div>
                      {lines.map(([label, value]) => (
                        <div key={label} className="mt-0.5 flex items-center gap-2">
                          <span className="text-muted-foreground">{label}</span>
                          <span className="ml-auto pl-3 font-medium tabular-nums">{value}</span>
                        </div>
                      ))}
                      {row.today && burndown.adjustmentCents !== 0 && (
                        <p className="mt-1 text-muted-foreground">
                          {t('admin.campaigns.submissions.budgetPace.adjustment', { amount: money(burndown.adjustmentCents) })}
                        </p>
                      )}
                    </div>
                  );
                }}
              />
              <Area
                dataKey="remaining"
                type="linear"
                stroke="var(--color-remaining)"
                fill="var(--color-remaining)"
                fillOpacity={0.12}
                strokeWidth={2}
                connectNulls={false}
                dot={false}
                activeDot={{ r: 3 }}
                isAnimationActive={false}
              />
              <Line
                dataKey="projected"
                type="linear"
                stroke="var(--color-projected)"
                strokeOpacity={0.75}
                strokeDasharray="4 3"
                strokeWidth={1.5}
                connectNulls
                dot={false}
                activeDot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ChartContainer>
        </div>

        {weeks.some((week) => week.views !== null) && (
          <div>
            <SectionLabel>{t('admin.campaigns.submissions.budgetPace.weeklyViews')}</SectionLabel>
            <ChartContainer config={viewsConfig} className="aspect-auto h-14 w-full" aria-label={t('admin.campaigns.submissions.budgetPace.weeklyViews')}>
              <BarChart data={weeks} margin={{ top: 2, right: 0, left: 0, bottom: 0 }} barCategoryGap="18%">
                <XAxis dataKey="week" hide />
                <YAxis hide />
                <ChartTooltip
                  cursor={{ fill: 'hsl(var(--muted))', opacity: 0.6 }}
                  content={({ active, payload }) => {
                    const week = payload?.[0]?.payload as (typeof weeks)[number] | undefined;
                    if (!active || !week) return null;
                    const params = { date: shortDay(week.week), views: new Intl.NumberFormat('pt-BR').format(week.value) };
                    return (
                      <div className="rounded-lg border bg-popover px-3 py-2 text-xs font-medium text-popover-foreground shadow-lg">
                        {week.partial
                          ? t('admin.campaigns.submissions.budgetPace.weeklyViewsTooltipPartial', params)
                          : t('admin.campaigns.submissions.budgetPace.weeklyViewsTooltip', params)}
                      </div>
                    );
                  }}
                />
                <Bar dataKey="value" fill="var(--color-views)" radius={[2, 2, 0, 0]} maxBarSize={28} isAnimationActive={false} />
              </BarChart>
            </ChartContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
